import Course from "@/models/Course";
import CourseRunDetails from "@/models/CourseRunDetails";
import CompoundCourseRunStep from "@/models/CompoundCourseRunStep";
import CompoundCoursePart from "@/models/CompoundCoursePart";
import moment from "moment/moment";

function parseTimestamp(value) {
    if (!value) {
        return null;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
}

export default class CourseRun {
    constructor(raw = {}) {
        console.log(raw);
        this.id = raw.id;
        this.course = new Course(raw.course);
        this.courseRunDetails = new CourseRunDetails(raw.courseRunDetails);
        this.state = raw.state;
        this.assignmentDate = raw.assignmentDate;
        this.isCompound = raw.isCompound;
        this.finishUntil = raw.finishUntil;
        this.compoundParts = raw.compoundParts ? raw.compoundParts.map(part => new CompoundCoursePart(part)) : [];
    }

    get hasRequirements() {
        return this.course.requireBirthdate || this.course.requireZvuExpirationDate || this.course.requireIsHimself
    }

    get isSuccess() {
        return this.courseRunDetails.certificateLink !== null && this.courseRunDetails.successDate
    }

    get blockStart() {
        return this.finishUntil != null && moment(this.finishUntil).isBefore(moment())
    }

    get isStartedButNotDone() {
        return !this.isDone && this.courseRunDetails.startDate != null;
    }

    get notStarted() {
        return this.courseRunDetails.startDate === null || this.courseRunDetails.startDate === undefined;
    }

    get isError() {
        return this.courseRunDetails.percentage === 100 && this.courseRunDetails.failureDate
    }

    get isDone() {
        return this.isSuccess || this.isError
    }

    get latestSyncTimestamp() {
        const timestamps = [parseTimestamp(this.courseRunDetails.lastSyncDate)]
            .concat(this.compoundParts.map((part) => part.lastSyncTimestamp))
            .filter((value) => value !== null);

        if (timestamps.length === 0) {
            return null;
        }

        return Math.max(...timestamps);
    }

    get latestSyncLabel() {
        return this.latestSyncTimestamp == null ? "Noch nie" : new Date(this.latestSyncTimestamp).toLocaleString("de-DE");
    }

    get hasSyncRisk() {
        if (this.isCompound) {
            return this.compoundParts.some((part) => part.hasSyncRisk);
        }

        if (!this.isStartedButNotDone) {
            return false;
        }

        return Boolean(this.courseRunDetails.syncIssueReason);
    }

    get syncRiskLabel() {
        if (this.isCompound) {
            const riskyParts = this.compoundParts.filter((part) => part.hasSyncRisk).map((part) => part.name);
            return riskyParts.length === 0 ? "Unauffaellig" : `Warnung in ${riskyParts.join(", ")}`;
        }

        if (!this.isStartedButNotDone) {
            return "Kein aktiver Kurs";
        }

        return this.hasSyncRisk ? this.courseRunDetails.syncIssueLabel : "Kein Sync-Problem";
    }

    get activeSyncEntries() {
        if (this.isCompound) {
            return this.compoundParts
                .filter((part) => part.isStartedButNotDone)
                .map((part) => ({
                    id: `${this.id}:${part.partId}`,
                    courseRunId: this.id,
                    partId: part.partId,
                    title: `${this.course.title} / ${part.name}`,
                    hasSyncRisk: part.hasSyncRisk,
                    lastSyncLabel: part.lastSyncLabel,
                    syncRiskLabel: part.syncRiskLabel,
                    hasPersistedState: part.hasPersistedState,
                }));
        }

        if (!this.isStartedButNotDone) {
            return [];
        }

        return [{
            id: this.id,
            courseRunId: this.id,
            partId: null,
            title: this.course.title,
            hasSyncRisk: this.hasSyncRisk,
            lastSyncLabel: this.latestSyncLabel,
            syncRiskLabel: this.syncRiskLabel,
            hasPersistedState: typeof this.state === "string" && this.state.length > 0,
        }];
    }

    stateAsJson() {
        return JSON.parse(this.state).cmi;
    }
}
